import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { http, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";

function StatusBadge({ status }) {
  const map = {
    pending: "bg-yellow-500/15 text-yellow-300 border-yellow-500/30",
    confirmed: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    cancelled: "bg-red-500/15 text-red-300 border-red-500/30",
    paid: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    unpaid: "bg-white/10 text-white/70 border-white/20",
  };
  return <span className={`text-[10px] px-2 py-1 rounded-full border uppercase tracking-widest ${map[status] || "bg-white/10 text-white/70 border-white/20"}`}>{status}</span>;
}

function downloadInvoice(b) {
  const items = b.fare?.items || [];
  const itemsRows = items.map((it) => `
    <tr>
      <td>
        <div style="font-weight:600;color:#040814">${it.label}</div>
        ${it.detail ? `<div style="font-size:11px;color:#666">${it.detail}</div>` : ""}
      </td>
      <td style="text-align:right;color:${it.amount < 0 ? "#15803d" : "#040814"};">${it.amount < 0 ? "−" : ""}€${Math.abs(it.amount).toFixed(2)}</td>
    </tr>`).join("");

  const html = `<!doctype html><html><head><meta charset="utf-8"><title>Invoice ${b.reference}</title>
  <style>
    body{font-family:Helvetica,Arial,sans-serif;color:#222;padding:48px;max-width:820px;margin:auto;background:#fff;}
    .head{display:flex;justify-content:space-between;align-items:flex-end;border-bottom:3px solid #D4AF37;padding-bottom:16px;margin-bottom:24px;}
    .brand{font-family:Georgia,serif;font-size:32px;color:#040814;}
    .brand .gold{color:#B5952F;font-style:italic;}
    .meta{text-align:right;font-size:13px;color:#555;}
    .meta b{color:#040814;}
    h2{font-family:Georgia,serif;color:#040814;font-size:18px;margin-top:24px;border-bottom:1px solid #eee;padding-bottom:6px;}
    table{width:100%;border-collapse:collapse;margin-top:8px;}
    td,th{padding:10px 12px;border-bottom:1px solid #eee;font-size:13px;vertical-align:top;}
    th{text-align:left;text-transform:uppercase;letter-spacing:1px;font-size:11px;color:#888;background:#fafafa;}
    .totalrow td{border:none;padding-top:18px;font-size:18px;font-weight:bold;color:#040814;}
    .totalrow td.gold{color:#B5952F;font-family:Georgia,serif;font-size:24px;}
    .grid{display:grid;grid-template-columns:1fr 1fr;gap:24px;margin-top:8px;font-size:13px;}
    .grid .lbl{color:#888;text-transform:uppercase;font-size:10px;letter-spacing:1px;margin-bottom:2px;}
    .footer{margin-top:48px;font-size:11px;color:#888;text-align:center;border-top:1px solid #eee;padding-top:16px;}
  </style></head><body>
  <div class="head">
    <div class="brand">Sam <span class="gold">Taxi</span></div>
    <div class="meta">
      <div><b>Invoice</b></div>
      <div>${b.reference}</div>
      <div>${(b.created_at || "").slice(0, 10)}</div>
    </div>
  </div>

  <div class="grid">
    <div>
      <div class="lbl">Billed to</div>
      <div><b>${b.customer_name || "-"}</b></div>
      <div>${b.customer_email || ""}</div>
      <div>${b.customer_phone || ""}</div>
    </div>
    <div>
      <div class="lbl">Issued by</div>
      <div><b>Koshsam Taxi</b></div>
      <div>samtaxi.online</div>
      <div>Amsterdam, Netherlands</div>
    </div>
  </div>

  <h2>Trip Details</h2>
  <table>
    <tr><th style="width:35%">Pickup</th><td>${b.pickup?.label || "-"}</td></tr>
    <tr><th>Destination</th><td>${b.destination?.label || "-"}</td></tr>
    <tr><th>Date / Time</th><td>${b.date} ${b.time}</td></tr>
    <tr><th>Trip type</th><td>${b.trip_type === "return" ? "Return" : "One-way"}</td></tr>
    <tr><th>Passengers / Luggage</th><td>${b.passengers} pax · ${b.luggage} bags</td></tr>
    <tr><th>Distance / Driving time</th><td>${b.fare?.distance_km} km · ${Math.round(b.fare?.duration_min || 0)} min</td></tr>
    ${b.airport_code ? `<tr><th>Airport</th><td>${b.airport_code} (${b.arrival_type || "international"})</td></tr>` : ""}
  </table>

  <h2>Charges</h2>
  <table>
    <thead><tr><th>Item</th><th style="text-align:right">Amount</th></tr></thead>
    <tbody>
      ${itemsRows}
      <tr class="totalrow"><td>Total Amount</td><td class="gold" style="text-align:right">€${b.fare?.total?.toFixed(2)}</td></tr>
    </tbody>
  </table>

  <div class="footer">
    Thank you for choosing Sam Taxi. Payment status: <b>${b.payment_status}</b>. BTW will be invoiced where applicable. Questions? info@samtaxi.online
  </div>
  </body></html>`;
  const blob = new Blob([html], { type: "text/html" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `invoice-${b.reference}.html`;
  a.click();
}

export default function CustomerDashboard() {
  const { user, refresh } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [params] = useSearchParams();
  const [showPw, setShowPw] = useState(false);
  const [pw, setPw] = useState({ current_password: "", new_password: "" });

  useEffect(() => {
    if (params.get("changepw") || user?.must_change_password) setShowPw(true);
  }, [params, user]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await http.get("/bookings/mine");
      setBookings(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const cancel = async (ref) => {
    if (!window.confirm("Cancel this booking?")) return;
    try {
      await http.post(`/bookings/${ref}/cancel`);
      toast.success("Cancelled");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const submitPw = async (e) => {
    e.preventDefault();
    try {
      await http.post("/auth/change-password", pw);
      toast.success("Password updated");
      setShowPw(false);
      refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  return (
    <div className="mx-auto max-w-7xl px-6 lg:px-10 py-16 text-white">
      <div className="text-xs tracking-[0.3em] uppercase text-[#D4AF37] mb-2">Dashboard</div>
      <h1 className="font-heading text-4xl mb-8">Welcome, {user?.name}</h1>

      <div className="rounded-xl border border-white/10 bg-[#0A1128] overflow-hidden">
        <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
          <h2 className="font-heading text-xl">Your bookings</h2>
          <span className="text-sm text-white/50">{bookings.length} total</span>
        </div>
        {loading ? (
          <div className="p-10 text-white/50 text-center">Loading...</div>
        ) : bookings.length === 0 ? (
          <div className="p-10 text-white/50 text-center">No bookings yet.</div>
        ) : (
          <div className="divide-y divide-white/10">
            {bookings.map((b) => (
              <div key={b._id} data-testid={`my-booking-${b.reference}`} className="px-6 py-5 grid md:grid-cols-6 gap-4 items-center">
                <div className="md:col-span-2">
                  <div className="font-mono text-[#D4AF37] text-sm">{b.reference}</div>
                  <div className="text-xs text-white/50 mt-1">{b.created_at?.slice(0, 16).replace("T", " ")}</div>
                </div>
                <div className="md:col-span-2 text-sm">
                  <div className="truncate">{b.pickup?.label}</div>
                  <div className="text-white/50 truncate">→ {b.destination?.label}</div>
                </div>
                <div className="text-sm">
                  <div>{b.date} {b.time}</div>
                  <div className="font-heading text-xl text-gold-gradient">€{b.fare?.total?.toFixed(2)}</div>
                </div>
                <div className="flex flex-col gap-2 items-start">
                  <div className="flex gap-2">
                    <StatusBadge status={b.status} />
                    <StatusBadge status={b.payment_status} />
                  </div>
                  <div className="flex gap-2 mt-1">
                    <Button data-testid={`invoice-${b.reference}`} variant="ghost" className="h-8 px-3 text-xs border border-white/15 hover:bg-white/5" onClick={() => downloadInvoice(b)}>Invoice</Button>
                    {b.status !== "cancelled" && b.payment_status !== "paid" && (
                      <Button data-testid={`cancel-${b.reference}`} variant="ghost" className="h-8 px-3 text-xs border border-red-500/30 text-red-300 hover:bg-red-500/10" onClick={() => cancel(b.reference)}>Cancel</Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={showPw} onOpenChange={setShowPw}>
        <DialogContent className="bg-[#0A1128] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Change password</DialogTitle>
            <DialogDescription className="text-white/60">Enter your current password and choose a new one (min 8 characters).</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitPw} className="space-y-4">
            <div><Label>Current password</Label><Input data-testid="cur-pw" required type="password" value={pw.current_password} onChange={(e) => setPw({...pw, current_password: e.target.value})} className="bg-[#040814] border-white/20" /></div>
            <div><Label>New password</Label><Input data-testid="new-pw" required type="password" minLength={8} value={pw.new_password} onChange={(e) => setPw({...pw, new_password: e.target.value})} className="bg-[#040814] border-white/20" /></div>
            <Button data-testid="submit-pw" type="submit" className="w-full bg-[#D4AF37] hover:bg-[#B5952F] text-[#040814]">Update</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
